import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '../../../components/button/Button';
import Input from '../../../components/input/Input';
import { useAuth } from '../../../context/AuthContext';
import { useBrandContext } from '../../../context/BrandContext';
import { useToast } from '../../../context/ToastModalContext';
import api from '../../../axios/axiosConfig';
import { CarBrand, CarModel, Inputs } from '../types/vehicles';
import Modal from '../../../components/modal/Modal';
import { useHeaderContext } from '../../../context/HeaderContext';
import { triggerGlobalVehiclesRefresh } from '../../../hooks/useVehiclesRefresh';
import './styles.css';

type Errors = Partial<Record<keyof Inputs | 'plate', string>>;

const normalizeUnknown = (name: string) =>
  name === '_unknown' ? 'Não listado' : name;

const VehicleForm = () => {
  const navigate = useNavigate();
  const { uvId } = useParams();
  const isEdit = Boolean(uvId);

  const brandContext = useBrandContext();
  const { brandBgColor } = brandContext;
  const authContext = useAuth();
  const { showToast } = useToast();

  const [errorsValidate, setErrorsValidate] = useState<Errors>({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [brands, setBrands] = useState<CarBrand[]>([]);
  const [models, setModels] = useState<CarModel[]>([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [vehicleDetail, setVehicleDetail] = useState<any>(null);

  const { setHeaderConfig } = useHeaderContext();

  const [form, setForm] = useState<Inputs>({
    uvAnuId: authContext?.user?.id,
    uvName: '',
    uvVmId: '',
    plate: '',
  });

  const [autoChargeItems, setAutochargeItems] = useState<any[]>([]);
  const [confirmDeleteVinModal, setConfirmDeleteVinModal] =
    useState<boolean>(false);
  const [confirmDeleteVehicleModal, setConfirmDeleteVehicleModal] =
    useState<boolean>(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);

  const validatePlate = (plate: string) => {
    const oldPlatePattern = /^[A-Z]{3}-[0-9]{4}$/; // AAA-1234
    const mercosulPlatePattern = /^[A-Z]{3}[0-9][A-Z][0-9]{2}$/; // AAA1A23
    const normalized = plate.replace(/-/g, '');
    return oldPlatePattern.test(plate) || mercosulPlatePattern.test(normalized);
  };

  const handlePlateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const originalValue = e.target.value
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');
    let formatted = '';
    const isOldPlateFormat = /^[A-Z]{3}[0-9]{4}$/.test(originalValue);

    if (originalValue.length <= 3) {
      formatted = originalValue;
    } else if (isOldPlateFormat) {
      formatted = `${originalValue.slice(0, 3)}-${originalValue.slice(3)}`;
    } else {
      formatted = originalValue;
    }

    setForm((prev) => ({ ...prev, plate: formatted }));

    const ok = validatePlate(formatted);
    setErrorsValidate((prev) => ({
      ...prev,
      plate: ok ? undefined : 'Placa inválida',
    }));
  };

  const getCarBrands = async (): Promise<CarBrand[]> => {
    try {
      const response = await api.get('/VehicleManufacturers');

      return response.data
        .sort((a: any, b: any) =>
          a.manufacturerName.localeCompare(b.manufacturerName)
        )
        .map((brand: any) => ({
          id:
            brand.manufacturerName === '_unknown'
              ? '0'
              : String(brand.manufacturerId),
          name: normalizeUnknown(brand.manufacturerName),
        }));
    } catch (error) {
      console.error('Error fetching car brands:', error);
      return [];
    }
  };

  const getCarModels = async (brandId: number): Promise<CarModel[]> => {
    try {
      const response = await api.get(
        `/VehicleModels/?manufacturerid=${brandId}`
      );

      return response.data
        .sort((a: any, b: any) => a.vmName.localeCompare(b.vmName))
        .map((model: any) => ({
          id: String(model.vmId),
          name: normalizeUnknown(model.vmName),
        }));
    } catch (error) {
      console.error('Error fetching car models:', error);
      return [];
    }
  };

  const getVehicle = async () => {
    if (!uvId) return null;
    try {
      const response = await api.get(`/UserVehicles/${uvId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching vehicle:', error);
      return null;
    }
  };

  const getAutoChargeItems = async () => {
    if (!uvId) return;
    try {
      const response = await api.get(`/ChargeTags/vin/vehicle/${uvId}`);
      setAutochargeItems(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const deleteAutoChargeItem = async () => {
    if (!uvId) return;
    try {
      setIsDeleteLoading(true);
      await api.delete(`/ChargeTags/vin/vehicle/${uvId}`);
      setConfirmDeleteVinModal(false);
      getAutoChargeItems();
    } catch (error) {
      console.error(error);
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const handleBrandChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedBrand(value);
    setSelectedModel('');
    setForm((prev) => ({ ...prev, uvVmId: '' }));

    if (value) {
      const list = await getCarModels(Number(value));
      setModels(list);
    } else {
      setModels([]);
    }
  };

  const handleModelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedModel(value);
    setForm((prev) => ({ ...prev, uvVmId: value }));
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validatePlate(form.plate)) {
      setErrorsValidate((prev) => ({ ...prev, plate: 'Placa inválida' }));
      return;
    }

    const payload = {
      uvAnuId: form.uvAnuId,
      uvVmId: form.uvVmId,
      uvName: form.uvName,
      uvLicencePlate: form.plate,
      ...(isEdit ? { uvId } : {}),
    };

    setIsSaving(true);

    try {
      const endpoint = isEdit ? `/UserVehicles/${uvId}` : '/UserVehicles';
      const method = isEdit ? api.put : api.post;

      const response = await method(endpoint, payload, {
        headers: {
          Authorization: `Bearer ${authContext.token}`,
          'Content-Type': 'application/json',
        },
      });

      const ok = [200, 201, 204].includes(response.status);
      if (ok) {
        // Trigger atualização da lista de veículos
        triggerGlobalVehiclesRefresh();
        
        // Recarrega dados do usuário e atualiza tags OneSignal
        await authContext.reloadUserData();
        
        navigate(-1);
        showToast({
          messageModal: isEdit
            ? 'Veículo atualizado com sucesso'
            : 'Veículo adicionado com sucesso',
          allowButtonClosed: true,
          closeButtonText: 'OK',
        });

        setForm({
          uvAnuId: authContext?.user?.id || '',
          uvName: '',
          uvVmId: '',
          plate: '',
        });
        setSelectedBrand('');
        setSelectedModel('');
      } else {
        console.error('Resposta inesperada ao salvar veículo', response);
      }
    } catch (error: any) {
      if (error?.response?.status === 409) {
        setErrorsValidate((prev) => ({
          ...prev,
          plate: 'Esta placa já está cadastrada. Verifique e tente novamente.',
        }));
      } else {
        console.error('Erro ao salvar veículo', error);
        showToast({
          messageModal:
            'Ocorreu um erro inesperado ao salvar o veículo. Tente novamente mais tarde.',
          allowButtonClosed: true,
          closeButtonText: 'OK',
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const onDeleteVehicle = async () => {
    if (!uvId) return;
    setIsDeleteLoading(true);
    try {
      const response = await api.delete(`/UserVehicles/${uvId}`);
      if ([200, 204].includes(response.status)) {
        // Trigger atualização da lista de veículos
        triggerGlobalVehiclesRefresh();
        
        // Recarrega dados do usuário e atualiza tags OneSignal
        await authContext.reloadUserData();
        
        navigate(-1);
        showToast({
          messageModal: 'Veículo excluído com sucesso',
          allowButtonClosed: true,
          closeButtonText: 'OK',
        });
      }
    } catch (error) {
      console.error('Erro ao excluir veículo:', error);
    } finally {
      setIsDeleteLoading(false);
    }
  };

  useEffect(() => {
    setForm((prev) => ({ ...prev, uvAnuId: authContext?.user?.id }));
  }, [authContext?.user?.id]);

  useEffect(() => {
    let mounted = true;

    const asyncHandler = async () => {
      setIsLoading(true);

      const [brandList, vehicle] = await Promise.all([
        getCarBrands(),
        getVehicle(),
      ]);
      if (!mounted) return;

      setBrands(brandList);

      if (vehicle) {
        setVehicleDetail(vehicle);
        setForm({
          uvAnuId: vehicle?.uvAnuId || authContext?.user?.id || '',
          uvName: vehicle?.uvName || '',
          uvVmId: vehicle?.uvVmId || '',
          plate: vehicle?.uvLicencePlate || '',
        });

        const manufacturer = vehicle?.uvVm?.vmManufacturer;
        const brandId =
          manufacturer?.manufacturerName === '_unknown'
            ? '0'
            : String(manufacturer?.manufacturerId || '');
        setSelectedBrand(brandId);

        if (brandId) {
          const list = await getCarModels(Number(brandId));
          if (!mounted) return;
          setModels(list);
          setSelectedModel(String(vehicle?.uvVmId || ''));
        }

        getAutoChargeItems();
      }

      setIsLoading(false);
    };

    asyncHandler();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uvId]);

  useEffect(() => {
    const ok =
      form.uvName?.trim() !== '' &&
      selectedBrand !== '' &&
      selectedModel !== '' &&
      validatePlate(form.plate);
    setIsFormValid(ok);
  }, [form.uvName, selectedBrand, selectedModel, form.plate]);

  useEffect(() => {
    setHeaderConfig({
      headerText: isEdit ? vehicleDetail?.uvName : 'Novo veículo',
    });
    return () => setHeaderConfig({});
  }, [navigate, setHeaderConfig, vehicleDetail?.uvName]);

  return (
    <>
      <form onSubmit={onSubmit} className='car-register-form'>
        <div>
          <Input
            labelName='Apelido'
            htmlFor='nick'
            type='text'
            id='nick'
            className={
              errorsValidate.uvName
                ? 'input-pattern error-input'
                : 'input-pattern'
            }
            placeholder='Apelido'
            value={form.uvName}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, uvName: e.target.value }))
            }
            isErrorValidate={!!errorsValidate.uvName}
            messageError={errorsValidate.uvName}
          />

          <div className='select'>
            <div className='select__container select__container--brand'>
              <label htmlFor='select-brand' className='select__label'>
                Marca*
              </label>
              <select
                id='select-brand'
                className='select__input'
                value={selectedBrand}
                onChange={handleBrandChange}
                disabled={isLoading}
              >
                <option value=''>Selecione</option>
                {brands.map((brand) => (
                  <option key={brand.id} value={brand.id}>
                    {brand.name}
                  </option>
                ))}
              </select>
            </div>

            <div className='select__container select__container--model'>
              <label htmlFor='select-model' className='select__label'>
                Modelo*
              </label>
              <select
                id='select-model'
                className='select__input'
                value={selectedModel}
                onChange={handleModelChange}
                disabled={!selectedBrand || isLoading}
              >
                <option value=''>Selecione</option>
                {models.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className='car-plate'>
            <label htmlFor='car-plate' className='car-plate__label'>
              Placa do Veículo
            </label>
            <input
              type='text'
              id='car-plate'
              className={`car-plate__input ${
                errorsValidate.plate ? 'error-input' : ''
              }`}
              maxLength={8}
              value={form.plate}
              onChange={handlePlateChange}
              placeholder='AAA-1234 ou AAA1A23'
            />
            {errorsValidate.plate && (
              <span className='error-message'>{errorsValidate.plate}</span>
            )}
          </div>

          {/* AutoCharge + Excluir somente em edição */}
          {isEdit && autoChargeItems.length > 0 && (
            <>
              <div className='car-autoRecharge'>
                <label
                  htmlFor='car-autoRecharge'
                  className='car-plate__label'
                  style={{ display: 'block', marginBottom: 5 }}
                >
                  AutoCharge
                </label>
                {autoChargeItems.map((item: any) => (
                  <div
                    key={item.tagId}
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      marginBottom: 15,
                    }}
                  >
                    <div style={{ width: '50%', boxSizing: 'border-box' }}>
                      <input
                        type='text'
                        id='car-autocharge'
                        className='car-plate__input'
                        value={item.tagId}
                        disabled
                        style={{
                          width: 'calc(100% - 20px)',
                          backgroundColor: '#e9e9ed',
                          fontWeight: 400,
                          fontSize: 14,
                        }}
                      />
                    </div>
                    <div
                      style={{
                        paddingLeft: 20,
                        width: '50%',
                        boxSizing: 'border-box',
                      }}
                    >
                      <Button
                        type='button'
                        className='btn'
                        style={{ marginBottom: 0, marginTop: -7 }}
                        onClick={() => setConfirmDeleteVinModal(true)}
                      >
                        Excluir
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ fontSize: 14, width: '50%', marginTop: -15 }}>
                Número VIN (Vehicle Identification Number) do seu veículo
              </div>
            </>
          )}

          <div
            style={{
              marginTop: 20,
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <Button
              type='submit'
              className='btn'
              spinnerSecondary
              disabled={!isFormValid || isLoading}
              loading={isSaving}
              style={{
                backgroundColor:
                  !isFormValid || isLoading
                    ? 'rgb(196, 196, 196)'
                    : brandBgColor,
                color: '#fff',
              }}
            >
              Salvar
            </Button>

            {isEdit && (
              <Button
                type='button'
                className='btn-outline'
                spinnerSecondary
                onClick={() => setConfirmDeleteVehicleModal(true)}
              >
                Excluir
              </Button>
            )}
          </div>
        </div>
      </form>

      <Modal
        allowButtonClosed
        isOpen={confirmDeleteVehicleModal}
        onClose={() => setConfirmDeleteVehicleModal(false)}
        title='Excluir Veículo'
        styleEnterButton={{
          backgroundColor: `${brandContext.brandBgColor}`,
          border: 'none',
        }}
        textEnterButton='Sim'
        closeButtonText='Cancelar'
        handleEnterButton={onDeleteVehicle}
        isLoadingEnterButton={isDeleteLoading}
      >
        <p>Deseja realmente excluir o veículo?</p>
      </Modal>

      <Modal
        allowButtonClosed
        isOpen={confirmDeleteVinModal}
        onClose={() => setConfirmDeleteVinModal(false)}
        title='Excluir AutoCharge'
        styleEnterButton={{
          backgroundColor: `${brandContext.brandBgColor}`,
          border: 'none',
        }}
        textEnterButton='Sim'
        closeButtonText='Cancelar'
        handleEnterButton={deleteAutoChargeItem}
        isLoadingEnterButton={isDeleteLoading}
      >
        <p>Deseja realmente excluir o AutoCharge do veículo?</p>
      </Modal>
    </>
  );
};

export default VehicleForm;
